import React, { useEffect } from 'react'
import { Button, Form, Modal } from 'semantic-ui-react'
import {
    FormCheckBoxField,
    FormDateField,
    FormDropdownField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements'
import { FlexRowBetween, TitleText } from 'components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { X } from 'phosphor-react'
import { DateFormat, DateUtility } from 'utility'
import { useNotification } from 'hooks'
import { useAuth } from 'context'

// Helper function to create enhanced options with "Select All"
const createEnhancedOptions = (options, fieldName) => {
    if (!options || options.length === 0) return []

    const selectAllOption = {
        key: `select_all_${fieldName}`,
        text: 'Select All',
        value: 'select_all',
    }
    const dividerOption = {
        key: `divider_${fieldName}`,
        text: '──────────',
        value: '',
        disabled: true,
    }

    return [selectAllOption, dividerOption, ...options]
}

// Helper function to handle select all logic
const handleSelectAllChange = (value, allValues, setValue, fieldName) => {
    if (Array.isArray(value)) {
        if (value.includes('select_all')) {
            // Select all options
            setValue(fieldName, allValues)
        } else {
            // Remove 'select_all' and '' (divider) from the value
            setValue(
                fieldName,
                value.filter(v => v !== 'select_all' && v !== ''),
            )
        }
    }
}

// Helper function to convert array to semicolon-separated string
const arrayToSemicolonString = array => {
    if (!array || !Array.isArray(array)) return ''
    return array.filter(item => item !== 'select_all' && item !== '').join(';')
}

const CustomerSchema = yup.object().shape({
    TemplateId: yup.mixed().test('has-value', 'Template is required', value => {
        if (Array.isArray(value)) {
            return (
                value.length > 0 &&
                value.some(v => v !== 'select_all' && v !== '')
            )
        }
        return !!value
    }),
    UserId: yup.mixed().test('has-value', 'Team Member is required', value => {
        if (Array.isArray(value)) {
            return (
                value.length > 0 &&
                value.some(v => v !== 'select_all' && v !== '')
            )
        }
        return !!value
    }),
    CustomerId: yup.mixed().test('has-value', 'Customer is required', value => {
        if (Array.isArray(value)) {
            return (
                value.length > 0 &&
                value.some(v => v !== 'select_all' && v !== '')
            )
        }
        return !!value
    }),
    InspectionDate: yup.string().required('Inspection Date is required'),
    SendEmailReminder: yup.boolean().default(true),
})
const TaskInspectionSearchSchema = yup.object().shape({
    UserId: yup.string(),
    Status: yup.string(),
    SearchText: yup.string(),
})

const AssignWorkSchema = yup.object().shape({
    UserId: yup.mixed().required('User name is required'),
    TemplateId: yup.mixed().required('Template is required'),
    AssignDate: yup.string().required('Assign date is required'),
})

export const TaskAddEditModal = ({
    onClose,
    open,
    data,
    loading,
    customerOptions,
    userseOptions,
    templateOptions,
    onSwitchToMaintenance,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CustomerSchema),
    })

    // Watch values for select all functionality
    const customerValue = watch('CustomerId') || []
    const teamMemberValue = watch('UserId') || []
    const templateValue = watch('TemplateId') || []

    // Create enhanced options with "Select All"
    const enhancedCustomerOptions = createEnhancedOptions(
        customerOptions,
        'CustomerId',
    )
    const enhancedTeamMemberOptions = createEnhancedOptions(
        userseOptions,
        'UserId',
    )
    const enhancedTemplateOptions = createEnhancedOptions(
        templateOptions,
        'TemplateId',
    )

    // Get all values for each field
    const allCustomerValues = customerOptions?.map(opt => opt.value) || []
    const allTeamMemberValues = userseOptions?.map(opt => opt.value) || []
    const allTemplateValues = templateOptions?.map(opt => opt.value) || []

    // Handle select all changes
    const handleCustomerChange = value => {
        handleSelectAllChange(value, allCustomerValues, setValue, 'CustomerId')
    }

    const handleTeamMemberChange = value => {
        handleSelectAllChange(value, allTeamMemberValues, setValue, 'UserId')
    }

    const handleTemplateChange = value => {
        handleSelectAllChange(value, allTemplateValues, setValue, 'TemplateId')
    }

    useEffect(() => {
        console.log('Data received in modal:', data)
        if (data?.TaskId) {
            reset({
                ...data,
                TaskDue: new Date(data.TaskDue),
                InspectionDate: new Date(data.InspectionDate),
                UserId: data.UserId ? [Number(data.UserId)] : [],
                TemplateId: data.TemplateId ? [data.TemplateId] : [],
                CustomerId: data.CustomerId ? [data.CustomerId] : [],
                SendEmailReminder: data.SendEmailReminder !== false,
            })
        } else {
            reset({
                UserId: [],
                TemplateId: [],
                CustomerId: [],
                SendEmailReminder: true,
            })
        }
    }, [data])
    console.log(data)

    const submit = async formData => {
        // Convert arrays to semicolon-separated strings for new fields
        const customerIds = arrayToSemicolonString(formData.CustomerId)
        const teamMemberIds = arrayToSemicolonString(formData.UserId)
        const templateIds = arrayToSemicolonString(formData.TemplateId)

        onClose({
            ...formData,
            // Keep original fields unchanged (take first value for backward compatibility)
            CustomerId: Array.isArray(formData.CustomerId)
                ? formData.CustomerId[0]
                : formData.CustomerId,
            UserId: Array.isArray(formData.UserId)
                ? formData.UserId[0]
                : formData.UserId,
            TemplateId: Array.isArray(formData.TemplateId)
                ? formData.TemplateId[0]
                : formData.TemplateId,
            // Add new fields for multiple selections
            CustomerIds: customerIds,
            TeamMemberIds: teamMemberIds,
            TemplateIds: templateIds,
            InspectionId: Array.isArray(formData.TemplateId)
                ? formData.TemplateId[0]
                : formData.TemplateId,
            TaskDue: formData.TaskDue
                ? DateUtility.dateToLocalISO(
                    new Date(formData.TaskDue),
                    DateFormat.MMDDYYYY,
                )
                : undefined,
            InspectionDate: DateUtility.dateToLocalISO(
                new Date(formData.InspectionDate),
                DateFormat.MMDDYYYY,
            ),
            SendEmailReminder: formData.SendEmailReminder,
        })
    }
    const onCancel = () => {
        onClose()
    }
    const modalTitle = data?.TaskId
        ? 'Edit Task Inspection'
        : 'Create Task Inspection'

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={() => onClose()}
            className="model"
            size="small"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{modalTitle}</TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {!data?.TaskId && onSwitchToMaintenance && (
                    <div className="mb-3">
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Task type</div>
                        <Button.Group size="small">
                            <Button type="button" primary>
                                Inspection
                            </Button>
                            <Button type="button" basic onClick={() => onSwitchToMaintenance()}>
                                Maintenance
                            </Button>
                        </Button.Group>
                    </div>
                )}
                <Form noValidate>
                    <div className="my-2">
                        <Form.Group widths="equal">
                            <FormTextFormField
                                control={control}
                                label="Title"
                                name="Title"
                                placeholder="Title"
                                errors={errors?.Title}
                                defaultValue=""
                            />
                            <FormDropdownField
                                control={control}
                                label="Customer"
                                name="CustomerId"
                                placeholder="Select Customer"
                                errors={errors?.CustomerId}
                                options={enhancedCustomerOptions}
                                search
                                multiple
                                required
                                onChange={(_, item) =>
                                    handleCustomerChange(item.value)
                                }
                                value={customerValue}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                label="Team Member"
                                name="UserId"
                                placeholder="Select TeamMember"
                                errors={errors?.UserId}
                                options={enhancedTeamMemberOptions}
                                search
                                multiple
                                required
                                onChange={(_, item) =>
                                    handleTeamMemberChange(item.value)
                                }
                                value={teamMemberValue}
                            />
                            <FormDropdownField
                                control={control}
                                label="Templates"
                                name="TemplateId"
                                placeholder="Select Templates"
                                errors={errors?.TemplateId}
                                options={enhancedTemplateOptions}
                                search
                                multiple
                                required
                                onChange={(_, item) =>
                                    handleTemplateChange(item.value)
                                }
                                value={templateValue}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDateField
                                control={control}
                                label="Inspection Date"
                                name="InspectionDate"
                                type="basic"
                                errors={errors?.InspectionDate}
                                position="top right"
                                required
                            />
                            <FormDateField
                                control={control}
                                label="Due Date "
                                name="TaskDue"
                                type="basic"
                                position="top right"
                                errors={errors?.TaskDue}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormCheckBoxField
                                control={control}
                                label="Send Email Reminder"
                                name="SendEmailReminder"
                                errors={errors?.SendEmailReminder}
                                defaultChecked
                            />
                        </Form.Group>
                    </div>
                    <SubmitCancelButtons
                        loading={loading}
                        onSubmit={handleSubmit(submit)}
                        onCancel={onCancel}
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}

export const AssignWorkForm = ({ userOptions, templateOptions, loading }) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(AssignWorkSchema),
    })

    const submit = async formData => {
        // For now just log; can be wired to API later
        console.log('Assign Work submit:', formData)
    }

    const resetForm = () => {
        reset({
            UserId: undefined,
            TemplateId: undefined,
            AssignDate: '',
        })
    }

    return (
        <Form noValidate>
            <div className="my-2">
                <Form.Group widths="equal">
                    <FormDropdownField
                        control={control}
                        label="User name"
                        name="UserId"
                        placeholder="Select user"
                        errors={errors?.UserId}
                        options={userOptions}
                        defaultValue=""
                        search
                    />
                    <FormDropdownField
                        control={control}
                        label="Template"
                        name="TemplateId"
                        placeholder="Select template"
                        errors={errors?.TemplateId}
                        options={templateOptions}
                        defaultValue=""
                        search
                    />
                    <FormDateField
                        control={control}
                        label="Assign date"
                        name="AssignDate"
                        placeholder="Select date"
                        errors={errors?.AssignDate}
                    />
                </Form.Group>
            </div>
            <SubmitCancelButtons
                loading={loading}
                onSubmit={handleSubmit(submit)}
                onCancel={resetForm}
            />
        </Form>
    )
}

const CustomerSchemas = yup.object().shape({
    Title: yup.string().required('Title is required'),
    CustomerId: yup.mixed().test('has-value', 'Customer is required', value => {
        if (Array.isArray(value)) {
            return (
                value.length > 0 &&
                value.some(v => v !== 'select_all' && v !== '')
            )
        }
        return !!value
    }),
    unique_id: yup
        .mixed()
        .test('has-value', 'Team Member is required', value => {
            if (Array.isArray(value)) {
                return (
                    value.length > 0 &&
                    value.some(v => v !== 'select_all' && v !== '')
                )
            }
            return !!value
        }),
    TemplateId: yup.mixed().test('has-value', 'Template is required', value => {
        if (Array.isArray(value)) {
            return (
                value.length > 0 &&
                value.some(v => v !== 'select_all' && v !== '')
            )
        }
        return !!value
    }),
    Frequency: yup
        .string()
        .oneOf(['Daily', 'Weekly', 'Monthly'], 'Invalid Frequency')
        .required('Frequency is required'),
    DueDays: yup.number().when('Frequency', {
        is: val => val === 'Weekly' || val === 'Monthly',
        then: yup
            .number()
            .required('Due Days is required')
            .min(1, 'Due Days must be at least 1')
            .max(7, 'Due Days must be at most 7'),
    }),
    Weekday: yup.string().when('Frequency', {
        is: 'Weekly',
        then: yup.string().required('Weekday is required'),
    }),
    MonthlyDate: yup.string().when('Frequency', {
        is: 'Monthly',
        then: yup.string().required('Monthly Date is required'),
    }),
    SendEmailReminder: yup.boolean().default(true),
    SendOnlyOnce: yup.boolean().default(false),
})

const getDaysInMonthOptions = () => {
    const days = Array.from({ length: 31 }, (_, i) => ({
        value: (i + 1).toString(),
        text: `Day ${i + 1}`,
    }))
    days.push({ value: 'Last', text: 'Last Day of Month' })
    return days
}

const getLastDayOfMonth = date => {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    return lastDay.getDate()
}

export const TaskAddScheduleModal = ({
    onClose,
    open,
    data,
    loading,
    customerOptions,
    userseOptions,
    templateOptions,
}) => {
    const { errorToast } = useNotification()
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        setError,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CustomerSchemas),
    })

    const selectedFrequency = watch('Frequency')
    const sendEmailReminder = watch('SendEmailReminder')

    // Watch values for select all functionality
    const customerValue = watch('CustomerId') || []
    const teamMemberValue = watch('unique_id') || []
    const templateValue = watch('TemplateId') || []

    // Create enhanced options with "Select All"
    const enhancedCustomerOptions = createEnhancedOptions(
        customerOptions,
        'CustomerId',
    )
    const enhancedTeamMemberOptions = createEnhancedOptions(
        userseOptions,
        'unique_id',
    )
    const enhancedTemplateOptions = createEnhancedOptions(
        templateOptions,
        'TemplateId',
    )

    // Get all values for each field
    const allCustomerValues = customerOptions?.map(opt => opt.value) || []
    const allTeamMemberValues = userseOptions?.map(opt => opt.value) || []
    const allTemplateValues = templateOptions?.map(opt => opt.value) || []

    // Handle select all changes
    const handleCustomerChange = value => {
        handleSelectAllChange(value, allCustomerValues, setValue, 'CustomerId')
    }

    const handleTeamMemberChange = value => {
        handleSelectAllChange(value, allTeamMemberValues, setValue, 'unique_id')
    }

    const handleTemplateChange = value => {
        handleSelectAllChange(value, allTemplateValues, setValue, 'TemplateId')
    }

    // const selectedMonthlyDate = watch('MonthlyDate')

    // useEffect(() => {
    //     console.log('Data received in Casual modal:', data) // Debug log
    //     if (data?.Id) {
    //         reset({
    //             ...data,
    //             unique_id: data.UserId ? Number(data.unique_id) : undefined,
    //             TemplateId: data.TemplateId || undefined,
    //             Frequency: data.Frequency || 'Daily',
    //             Weekday: data.Weekday ? String(data.Weekday) : '',
    //             MonthlyDate: data.MonthlyDate ? String(data.MonthlyDate) : '1',
    //             DueDays: '1',
    //         })
    //     } else {
    //         reset({
    //             Frequency: 'Daily',
    //             Weekday: '',
    //             MonthlyDate: '1',
    //             DueDays: '1',
    //         })
    //     }
    // }, [data, reset])

    useEffect(() => {
        console.log('Data received in Casual modal:', data) // Debug log
        if (data?.Id) {
            // Map Frequency value to string
            const frequencyMap = {
                1: 'Daily',
                2: 'Weekly',
                3: 'Monthly',
            }
            const frequency = frequencyMap[data.Frequency] || 'Daily'

            // Transform data for the form
            const formData = {
                Id: data.Id,
                Title: data.Title,
                CustomerId: data.CustomerId ? [data.CustomerId] : [],
                unique_id: data.TeamMember ? [data.TeamMember] : [],
                TemplateId: data.TemplateId ? [data.TemplateId] : [],
                Frequency: frequency, // Map Frequency to string
                Weekday: data.Weekday || '1', // Ensure Weekday is a string
                MonthlyDate: data.MonthlyDate ? String(data.MonthlyDate) : '1', // Ensure MonthlyDate is a string
                DueDays: data.DueDays || '1', // Ensure DueDays is a string
                SendEmailReminder: data.SendEmailReminder !== false,
                SendOnlyOnce: data.SendOnlyOnce || false,
            }

            console.log('Transformed form data:', formData) // Debug log
            reset(formData)
        } else {
            reset({
                Frequency: 'Daily',
                Weekday: '',
                MonthlyDate: '1',
                DueDays: '1',
                CustomerId: [],
                unique_id: [],
                TemplateId: [],
                SendEmailReminder: true,
                SendOnlyOnce: false,
            })
        }
    }, [data, reset])

    const onSubmit = async formData => {
        console.log('Form data before processing:', formData) // Debug log

        // Hard guards: ensure required multiselects have at least one real value
        const hasTemplate = Array.isArray(formData.TemplateId)
            ? formData.TemplateId.some(v => v && v !== 'select_all' && v !== '')
            : !!formData.TemplateId
        const hasCustomer = Array.isArray(formData.CustomerId)
            ? formData.CustomerId.some(v => v && v !== 'select_all' && v !== '')
            : !!formData.CustomerId
        if (!hasTemplate || !hasCustomer) {
            if (!hasTemplate) {
                setError('TemplateId', { type: 'required', message: 'Template is required' })
            }
            if (!hasCustomer) {
                setError('CustomerId', { type: 'required', message: 'Customer is required' })
            }
            try { errorToast('Please fill all required fields') } catch { /* no-op */ }
            return
        }

        const frequencyMap = {
            Daily: 1,
            Weekly: 2,
            Monthly: 3,
        }

        const monthlyDateValue =
            formData.MonthlyDate === 'Last'
                ? getLastDayOfMonth(new Date())
                : parseInt(formData.MonthlyDate, 10)

        // Convert arrays to semicolon-separated strings for new fields
        const customerIds = arrayToSemicolonString(formData.CustomerId)
        const teamMemberIds = arrayToSemicolonString(formData.unique_id)
        const templateIds = arrayToSemicolonString(formData.TemplateId)

        const payload = {
            ...formData,
            // Keep original fields unchanged (take first value for backward compatibility)
            CustomerId: Array.isArray(formData.CustomerId)
                ? formData.CustomerId[0]
                : formData.CustomerId,
            TeamMember: Array.isArray(formData.unique_id)
                ? formData.unique_id[0]
                : formData.unique_id,
            TemplateId: Array.isArray(formData.TemplateId)
                ? formData.TemplateId[0]
                : formData.TemplateId,
            // Add new fields for multiple selections
            CustomerIds: customerIds,
            TeamMemberIds: teamMemberIds,
            TemplateIds: templateIds,
            Frequency: frequencyMap[formData.Frequency],
            InspectionId: Array.isArray(formData.TemplateId)
                ? formData.TemplateId[0]
                : formData.TemplateId,
            Weekday:
                selectedFrequency === 'Weekly'
                    ? parseInt(formData.Weekday, 10)
                    : undefined,
            MonthlyDate:
                selectedFrequency === 'Monthly' ? monthlyDateValue : undefined,
            DueDays: formData.DueDays
                ? parseInt(formData.DueDays, 10)
                : undefined,
            SendEmailReminder: formData.SendEmailReminder,
            SendOnlyOnce: formData.SendOnlyOnce || false,
        }

        // Remove undefined properties
        Object.keys(payload).forEach(
            key => payload[key] === undefined && delete payload[key],
        )

        console.log('Submitting data:', payload)
        onClose(payload)
    }

    const onCancel = () => {
        onClose()
    }

    const modalTitle = data?.Id
        ? 'Edit Schedule Task Inspection'
        : 'Schedule Task Inspection'

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            className="model"
            size="small"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{modalTitle}</TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onCancel()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <div className="my-2">
                        <Form.Group widths="equal">
                            <FormTextFormField
                                control={control}
                                label="Title"
                                name="Title"
                                placeholder="Title"
                                errors={errors?.Title}
                                defaultValue=""
                                required
                            />
                            <FormDropdownField
                                control={control}
                                label="Customer"
                                name="CustomerId"
                                placeholder="Select Customer"
                                errors={errors?.CustomerId}
                                options={enhancedCustomerOptions}
                                search
                                multiple
                                required
                                onChange={(_, item) =>
                                    handleCustomerChange(item.value)
                                }
                                value={customerValue}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                label="Team Member"
                                name="unique_id"
                                placeholder="Select Team Member"
                                errors={errors?.unique_id}
                                options={enhancedTeamMemberOptions}
                                search
                                multiple
                                required
                                onChange={(_, item) =>
                                    handleTeamMemberChange(item.value)
                                }
                                value={teamMemberValue}
                            />
                            <FormDropdownField
                                control={control}
                                label="Templates"
                                name="TemplateId"
                                placeholder="Select Templates"
                                errors={errors?.TemplateId}
                                options={enhancedTemplateOptions}
                                search
                                multiple
                                required
                                onChange={(_, item) =>
                                    handleTemplateChange(item.value)
                                }
                                value={templateValue}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                label="Frequency"
                                name="Frequency"
                                placeholder="Select Frequency"
                                errors={errors?.Frequency}
                                options={[
                                    { value: 'Daily', text: 'Daily' },
                                    { value: 'Weekly', text: 'Weekly' },
                                    { value: 'Monthly', text: 'Monthly' },
                                ]}
                                required
                            />
                        </Form.Group>

                        {selectedFrequency === 'Weekly' && (
                            <Form.Group widths="equal">
                                <FormDropdownField
                                    control={control}
                                    label="Select Weekday"
                                    name="Weekday"
                                    placeholder="Select Weekday"
                                    errors={errors?.Weekday}
                                    options={[
                                        { value: 1, text: 'Monday' },
                                        { value: 2, text: 'Tuesday' },
                                        { value: 3, text: 'Wednesday' },
                                        { value: 4, text: 'Thursday' },
                                        { value: 5, text: 'Friday' },
                                        { value: 6, text: 'Saturday' },
                                        { value: 7, text: 'Sunday' },
                                    ]}
                                    required
                                />
                                <FormDropdownField
                                    control={control}
                                    label="Due Within (Days)"
                                    name="DueDays"
                                    placeholder="Select Days (1-7)"
                                    errors={errors?.DueDays}
                                    options={[
                                        { value: 1, text: '1 Day' },
                                        { value: 2, text: '2 Days' },
                                        { value: 3, text: '3 Days' },
                                        { value: 4, text: '4 Days' },
                                        { value: 5, text: '5 Days' },
                                        { value: 6, text: '6 Days' },
                                        { value: 7, text: '7 Days' },
                                    ]}
                                    required
                                />
                            </Form.Group>
                        )}
                        {selectedFrequency === 'Monthly' && (
                            <Form.Group widths="equal">
                                <FormDropdownField
                                    control={control}
                                    label="Select Day of Month"
                                    name="MonthlyDate"
                                    placeholder="Select Day (1-31 or Last Day of Month)"
                                    errors={errors?.MonthlyDate}
                                    options={getDaysInMonthOptions()}
                                    required
                                />
                                <FormDropdownField
                                    control={control}
                                    label="Due Within (Days)"
                                    name="DueDays"
                                    placeholder="Select Days (1-7)"
                                    errors={errors?.DueDays}
                                    options={[
                                        { value: 1, text: '1 Day' },
                                        { value: 2, text: '2 Days' },
                                        { value: 3, text: '3 Days' },
                                        { value: 4, text: '4 Days' },
                                        { value: 5, text: '5 Days' },
                                        { value: 6, text: '6 Days' },
                                        { value: 7, text: '7 Days' },
                                    ]}
                                    required
                                />
                            </Form.Group>
                        )}
                        <Form.Group widths="equal">
                            <FormCheckBoxField
                                control={control}
                                label="Send Email Reminder"
                                name="SendEmailReminder"
                                errors={errors?.SendEmailReminder}
                                defaultChecked
                            />
                        </Form.Group>
                        {sendEmailReminder && (
                            <Form.Group widths="equal">
                                <FormCheckBoxField
                                    control={control}
                                    label="Send Only once"
                                    name="SendOnlyOnce"
                                    errors={errors?.SendOnlyOnce}
                                />
                            </Form.Group>
                        )}
                    </div>
                    <SubmitCancelButtons
                        loading={Boolean(loading)}
                        onSubmit={handleSubmit(onSubmit)}
                        onCancel={onCancel}
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}

export const TaskSearchForm = ({
    filterChanged,
    loading,
    statusOptions,
    userseOptions,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(TaskInspectionSearchSchema),
    })
    const { user } = useAuth()
    const submit = async formData => {
        filterChanged(formData)
    }
    const resetForm = () => {
        reset({ SearchText: '', UserId: undefined })
        filterChanged({ SearchText: '', UserId: '', Status: 0 })
    }
    return (
        <Form noValidate>
            <div className="my-2">
                <Form.Group widths="equal">
                    <FormTextFormField
                        control={control}
                        label="Search"
                        name="SearchText"
                        placeholder="Search"
                        errors={errors?.SearchText}
                        defaultValue=""
                    />
                    <FormDropdownField
                        control={control}
                        label="Status"
                        name="Status"
                        placeholder="Status"
                        errors={errors?.Status}
                        options={statusOptions}
                        defaultValue={0}
                        search
                    />
                    <FormDropdownField
                        control={control}
                        label={user.CompanyId === 34930 ? 'Auditor Name' : 'User ID'}
                        name="UserId"
                        placeholder="Select User"
                        errors={errors?.UserId}
                        options={userseOptions}
                        defaultValue=""
                    />
                </Form.Group>
            </div>
            <SubmitCancelButtons
                loading={loading}
                onSubmit={handleSubmit(submit)}
                onCancel={resetForm}
            />
        </Form>
    )
}
